/**
 * Build the `<title>` subject for a tender detail page.
 *
 * Source titles are raw ИКПУ line-item lists, semicolon-joined, and they are LONG:
 * across the 2,593 live tenders in prod on 2026-07-31 the mean is 108 characters,
 * the longest is 1,114, and 65% exceed 68. Google shows roughly 60 including the
 * " — UzTender.com" suffix, so the visible part was a truncated list of parts —
 * "Контактор; Провод алюминиевый общего назначения; Плинтус потолочный…".
 *
 * Taking only the FIRST line item is what fixes that. Measured over 100 live
 * tenders from the catalog API, full rendered title length including the suffix:
 *
 *   whole title, capped at 68   median 77, max 83, 97/100 over 60 chars
 *   first item, capped at 45    median 55, max 60,   0/100 over 60 chars
 *
 * ## Why the buyer is NOT appended
 *
 * The obvious idea — lead with the item, then the buying organisation, since that
 * is the entity a supplier recognises — was built and measured, and the numbers
 * rejected it. With a 60-character budget and a 15-character brand suffix, a
 * useful subject leaves no room: a "first item + buyer if it fits" variant scored
 * IDENTICALLY to first-item-alone, because the buyer never once fit. Forcing it in
 * produced two truncated fragments joined by a dash, and made already-short titles
 * worse — a 45-character title that displayed fine became 96 characters of
 * "Foriš t. Oktepa MFYda issiqxona tashkil… — FORISH TUMAN HOKIMLIGI OBODONLASHTIRI…".
 * Registry buyer names are long, ALL-CAPS and quoted, which compounds it.
 *
 * The buyer stays in the H1, the body and the JSON-LD, where length is not scored.
 *
 * ## Why the BUDGET is appended (2026-08-03)
 *
 * Subject-alone is short and readable, but it is not unique: the source titles are
 * ИКПУ product names, and thousands of genuinely distinct lots share one. Measured
 * as a census of the whole indexed set (2,517 active + 4,277 closed-awarded = 6,794
 * pages), running this exact function:
 *
 *   current (subject only)   66.2% of pages share a title   median 54 chars
 *   + region                 67.1%  (WORSE — lots cluster)   median 64
 *   + buyer (clipped to 28)  49.5%                           median 67
 *   + buyer (full)           42.4%                           median 98
 *   + budget                 37.0%                           median 58
 *   + buyer + budget         19.9%                           median 80
 *
 * 484 pages rendered the identical "Услуги по оптовой и розничной торговле и…".
 *
 * The budget wins because it is the only differentiator that is SHORT. It works
 * within the buyer decision above rather than reversing it: the buyer is still too
 * long, the budget is not. It is also the fact a supplier scans a result list for —
 * whether the lot is their size — so it earns its characters twice.
 *
 * ### Why the subject is cut to 28 and not 34
 *
 * The 37.0% row above used a 34-char subject, which pushed 26% (ru) / 35% (uz) of
 * rendered titles PAST 60 characters — breaking the very invariant that justified
 * rejecting the buyer. Swept over the same 6,794 pages, rendered length including
 * the suffix:
 *
 *   cap 28   dup 42.4%   median 51   max 60/61   over-60: 0% ru / 0% uz   ← chosen
 *   cap 30   dup 41.2%   median 53   max 62/63   over-60: 2% / 5%
 *   cap 32   dup 38.7%   median 57   max 64/65   over-60: 10% / 20%
 *   cap 34   dup 37.1%   median 58   max 65/66   over-60: 26% / 35%
 *   cap 36   dup 33.7%   median 60   max 68/69   over-60: 44% / 53%
 *
 * 28 keeps the module's original "nothing exceeds Google's display budget" property
 * intact while still removing a third of all duplicate titles (66.2% → 42.4%, and
 * 2,731 → 4,712 distinct titles). The extra 5.3 points of de-duplication that a
 * 34-char cap buys is not worth putting a third of the catalog over the display
 * budget — that would trade a measured, documented invariant for an unmeasured hope.
 *
 * Consolidating same-title lots under one canonical was considered instead and
 * rejected on measurement: of the groups such a rule would merge, 86% differ in
 * budget, 86% in final price and 70% in winner. They share a name; they are not
 * duplicates.
 */

/**
 * Longest subject kept when the title stands alone. 45 + " — UzTender.com" (15) =
 * 60, exactly Google's display budget, and the measurement above shows nothing
 * exceeds it.
 */
export const SUBJECT_BUDGET = 45;

/**
 * Longest subject kept when a budget is appended, so that
 * `{subject} — {budget} — UzTender.com` still fits the SAME 60-character budget.
 * Budget labels run up to ~14 chars ("181,3 млн сум", "1,9 mlrd so'm"), so
 * 28 + 3 + 14 + 15 = 60. Verified over all 6,794 indexed pages: max rendered length
 * is 60 (ru) / 61 (uz), 0% over budget. See the sweep in the module header.
 */
export const SUBJECT_BUDGET_WITH_MONEY = 28;

/**
 * The list separator is ';' — the first entry is the lot's headline item. A title
 * without ';' is already a single subject and passes through untouched.
 */
function firstLineItem(title: string): string {
  return title.split(';')[0]?.trim() || title.trim();
}

/** Truncate on a word boundary, trimming dangling punctuation before the ellipsis. */
function clip(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const slice = t.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.replace(/[\s,;:.—-]+$/, '')}…`;
}

/**
 * @param title       raw source title (a semicolon-joined ИКПУ line-item list)
 * @param budgetLabel already-formatted, already-localized money string including the
 *                    unit — e.g. "12 млн сум" / "1,9 mlrd so'm". Formatting stays at
 *                    the call site because that is where `locale` and `t()` live;
 *                    this module has no i18n dependency and stays a pure function.
 *                    Pass null/undefined when the lot has no budget.
 */
export function buildTenderSeoTitle(title: string, budgetLabel?: string | null): string {
  const money = budgetLabel?.trim();
  if (!money) return clip(firstLineItem(title), SUBJECT_BUDGET);

  // Only tighten the subject when it would actually overflow — a short title like
  // "Фотоэлектрическая панель" must not grow an ellipsis just because a budget follows.
  const subject = clip(firstLineItem(title), SUBJECT_BUDGET_WITH_MONEY);
  return subject ? `${subject} — ${money}` : money;
}
